import React from "react";
import Flexbox from "flexbox-react";
import { Card, Elevation, Divider } from "@blueprintjs/core";
import "./MyAssessmentView.css";
import { isGoalProgressSpecialAccount } from "../../utils/goalProgressUtils.js";
import CategoryRemarkInput from "./CategoryRemarkInput";

const AssessmentReadOnlyList = ({
  data,
  finalAssessmentRating,
  weightage,
  isRatingEnabledQuestions,
  accountId,
  ratingLabels = [],
  visibilityConfig,
  isAnswerAllowedOnQuestion,
  hideCategoryHeader = false,
  hideRatingAndWeightage = false
}) => {

  const effectiveRatingLabels = ratingLabels || [];


  const normalizedCategories = React.useMemo(() => {

    if (!Array.isArray(data)) return [];

    if (data[0]?.answered_assessment) {
      return data;
    }

    if (data[0]?.questions) {
      const basenameFromUrl = (url) => {
        try {
          if (!url || typeof url !== "string") return "";
          const withoutQuery = url.split("?")[0];
          const parts = withoutQuery.split("/");
          return parts[parts.length - 1] || "";
        } catch (_) {
          return "";
        }
      };

      return data.map(cat => {

        const isNullCategory =
          cat.category_id == null ||
          cat.category_name == null ||
          cat.category_name == "null";

        return {
          category_id: isNullCategory ? "uncategorized" : cat.category_id,
          category_name: isNullCategory ? "" : cat.category_name,
          average_rating: cat.average_rating,
          average_rating_label: cat.average_rating_label,
          remark_questions: [
            ...(cat.remark_questions || []),
            ...(cat.remark_answers || [])
          ]
            .map(remark => ({
              ...remark,
              question: remark.question || remark.remark_question || "",
              answer: remark.answer || ""
            }))
            .filter(remark => remark.question?.trim()),

          answered_assessment: (cat.questions || []).map(q => ({
            id: q.id,
            assessment_question: q.question,
            answer_type: q.answer_type,
            assessment_value:
              q.answer_type === "Files"
                ? (q.file_name || q.assessment_value || basenameFromUrl(q.file_url || q.answer))
                : (q.assessment_value != null ? q.assessment_value : q.value || ""),
            file_url: q.file_url || q.answer || "",
            file_name: q.file_name || basenameFromUrl(q.file_url || q.answer),
            rating: q.rating,
            assessment_question_weightage: q.weightage
          }))
        };
      });
    }

    // ⭐ CASE 3 → Flat answered list
    return [{
      category_name: "",
      answered_assessment: data
    }];

  }, [data]);


  const renderAnswer = entry => {
    const { answer_type, assessment_value } = entry;

    if (answer_type === "Checkbox") {
      let checkboxValues = {};

      // Handle both object and string cases
      if (typeof assessment_value === "object" && assessment_value !== null) {
        checkboxValues = assessment_value;
      } else if (typeof assessment_value === "string" && assessment_value.trim()) {
        try {
          checkboxValues = JSON.parse(assessment_value);
        } catch (e) {
          // If parsing fails, it's just a plain string value
          return (
            <div className="read-only-answer-box">
              <div className="checkbox-icon" />
              <span className="read-only-answer-text">{assessment_value}</span>
            </div>
          );
        }
      }

      // If no values selected
      if (Object.keys(checkboxValues).length === 0) {
        return <span className="read-only-answer-text">-</span>;
      }

      return Object.values(checkboxValues).map((val, idx) => (
        <div key={idx} className="read-only-answer-box">
          <div className="checkbox-icon" />
          <span className="read-only-answer-text">{val}</span>
        </div>
      ));
    }

    if (answer_type === "Radio") {
      return (
        <div className="read-only-answer-box">
          <div className="radio-icon">
            <div className="radio-dot" />
          </div>
          <span className="read-only-answer-text">{assessment_value}</span>
        </div>
      );
    }

    if (answer_type === "DropDown") {
      return (
        <div className="read-only-answer-box">
          <span className="read-only-answer-text">{assessment_value}</span>
        </div>
      );
    }

    if (answer_type === "ShortAnswer" || answer_type === "TextBox") {
      return (
        <div className="read-only-answer-box">
          <span className="read-only-answer-text">
            {assessment_value || "-"}
          </span>
        </div>
      );
    }

    if (answer_type === "Files") {
      const fileUrl = entry?.file_url || "";
      const fileName =
        entry?.file_name ||
        assessment_value ||
        "Download File";

      if (!fileUrl) {
        return <span className="read-only-answer-text">-</span>;
      }

      return (
        <div >
          <a
            href={fileUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="read-only-answer-text"
            style={{ color: "#106ba3", textDecoration: "underline", cursor: "pointer" }}
          >
            {fileName}
          </a>
        </div>
      );
    }

    return <div>{assessment_value || "-"}</div>;
  };

  const shouldHideAnswer = (entry) => {
    const {
      specialAccountId,
      allowedQuestionId
    } = visibilityConfig || {};

    const allowedIds = Array.isArray(allowedQuestionId)
      ? allowedQuestionId
      : [allowedQuestionId].filter(id => id !== undefined);

    const questionId =
      entry?.assessment_question_id || entry?.id;

    const answerAllowedOnQuestion =
      isAnswerAllowedOnQuestion == null
        ? true
        : Number(isAnswerAllowedOnQuestion) === 1;

    const shouldHideByCycleSetting =
      isAnswerAllowedOnQuestion != null && !answerAllowedOnQuestion;

    const shouldHideBySpecialAccount =
      answerAllowedOnQuestion &&
      accountId === specialAccountId &&
      allowedIds.length > 0 &&
      !allowedIds.includes(questionId);

    return shouldHideByCycleSetting || shouldHideBySpecialAccount;
  };

  return (
    <div>
      {normalizedCategories.map((category, catIndex) => (
        <div key={category.category_id || catIndex}>
          {(() => {
            const isUncategorized =
              category?.category_id === "uncategorized" ||
              category?.category_id == null ||
              category?.category_id === "null";
            const headerText = isUncategorized ? "Uncategorized" : (category?.category_name || "");

            if (hideCategoryHeader || !headerText) return null;

            return (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div
                  className="category-header"
                  style={{
                    fontWeight: "600",
                    fontSize: "15px",
                    color: "#1f4fd9",
                  }}
                >
                  {headerText}
                </div>
                {!hideRatingAndWeightage && isRatingEnabledQuestions == 1 &&
                  (category.average_rating != null && category.average_rating !== undefined) && (
                    <div>
                      <p className="answer-rating"
                        style={{
                          fontSize: "18px",
                          margin: "0px 10px 10px",
                          fontWeight: "600",
                          color: "#1f4fd9",
                        }}
                      >
                        <strong>
                          {Number(category.average_rating).toFixed(2)}
                        </strong>
                      </p>
                      {/* {category.average_rating_label ? (
                      <div
                        style={{
                          marginTop: -8,
                          marginRight: 10,
                          textAlign: "right",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#5c7080",
                          textTransform: "capitalize"
                        }}
                      >
                        {category.average_rating_label}
                      </div>
                    ) : null} */}
                    </div>
                  )}
              </div>
            );
          })()}

          {category?.answered_assessment?.map((entry, index) => {
            if (!entry) return null;
            return (
            <Flexbox flexDirection="column" key={entry.id || index}>

              <Flexbox
                justifyContent="space-between"
                className="assessment-answer-card"
                gap="20px"
              >
                <Flexbox flexDirection="column" className="answer-container">
                  <p className="assessment-question-text">
                    {entry.assessment_question ||
                      entry.manager_assessment_question ||
                      entry.question ||
                      "-"}
                  </p>

                  {!shouldHideAnswer(entry) && (
                    <div style={{ marginTop: 9 }}>
                      {renderAnswer(entry)}
                    </div>
                  )}

                </Flexbox>

                {!hideRatingAndWeightage && isRatingEnabledQuestions == 1 && <>
                  <Flexbox flexDirection="column" className="read-only-rating-container">
                    <p className="answer-weightage">
                      Rating:{" "}
                      <strong>
                        {(() => {
                          const rating = entry.rating;
                          if (rating === -1 || String(rating) === "-1") return "NA";
                          const useLabels = isGoalProgressSpecialAccount(accountId);
                          if (useLabels && Array.isArray(effectiveRatingLabels) && effectiveRatingLabels.length > 0) {
                            return effectiveRatingLabels.find(rl => Number(rl.value) === Number(rating))?.label ?? (rating ?? "-");
                          }
                          return rating ?? "-";
                        })()}
                      </strong>
                    </p>
                    {weightage &&
                      <p className="answer-weightage">
                        Weightage:{" "}
                        <strong style={{ fontWeight: 600 }}>
                          {entry.assessment_question_weightage ?? "-"}
                        </strong>
                      </p>
                    }
                  </Flexbox>
                </>}
              </Flexbox>
            </Flexbox>
          )})}


          {(() => {
            const visibleRemarks = (category.remark_questions || []).filter(
              remark => remark?.question?.trim()
            );

            if (visibleRemarks.length === 0) {
              return null;
            }

            return (
              <div style={{ marginTop: 12, marginLeft: 10 }}>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    marginBottom: "6px",
                    color: "#5c7080"
                  }}
                >
                  Comment
                </div>

                {visibleRemarks.map((remark) => (
                  <CategoryRemarkInput
                    width="60%"
                    key={remark.id}
                    question={remark.question}
                    value={remark.answer}
                    readOnly={true}
                    multiline={true}
                  />
                ))}
              </div>
            );
          })()}
          {catIndex !== normalizedCategories.length - 1 && (
            <Divider style={{ margin: "20px 0", marginTop: 0 }} />
          )}
        </div>
      ))}
    </div>
  );
};


export default AssessmentReadOnlyList;
